import React, { useState, useMemo } from 'react';
import { Autocomplete, TextField, Box, CircularProgress, Typography, InputAdornment, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PersonIcon from '@mui/icons-material/Person';
import PostAddIcon from '@mui/icons-material/PostAdd';
import * as API from '../../api';

const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            func.apply(null, args);
        }, delay);
    };
};

const FastSearchSection = ({ 
    onSelectCustomer, 
    onSelectBooking,  
    currentCustomer,  
    currentBookingCode, 
    isLocked 
}) => {
    const [globalOptions, setGlobalOptions] = useState([]);
    const [globalLoading, setGlobalLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const [bkOptions, setBkOptions] = useState([]);
    const [bkLoading, setBkLoading] = useState(false);
    const [bkInputValue, setBkInputValue] = useState('');

    const fetchGlobalSearch = useMemo(() => debounce(async (keyword) => {
        if (!keyword || keyword.length < 2) { setGlobalOptions([]); return; }
        setGlobalLoading(true);
        try {
            const [custRes, bkRes] = await Promise.all([
                API.searchCustomers(keyword),
                API.searchBookings(keyword)
            ]);

            const customers = (custRes.data || []).map(c => ({ ...c, dataType: 'CUSTOMER' }));
            const bookings = (bkRes.data || []).map(b => ({ ...b, dataType: 'BOOKING' }));

            setGlobalOptions([...customers, ...bookings]);
        } catch (err) { console.error(err); } 
        finally { setGlobalLoading(false); }
    }, 400), []);

    const fetchBookingsOnly = useMemo(() => debounce(async (keyword) => {
        if (!keyword) { setBkOptions([]); return; }
        setBkLoading(true);
        try {
            const res = await API.searchBookings(keyword);
            setBkOptions(res.data || []);
        } catch (err) { console.error(err); } 
        finally { setBkLoading(false); }
    }, 300), []);

    const computedBkOptions = useMemo(() => {
        if (!bkInputValue) return bkOptions;

        const exactMatch = bkOptions.find(opt => opt.booking_code.toLowerCase() === bkInputValue.toLowerCase());
        
        const options = [...bkOptions];
        
        if (!exactMatch) {
            options.push({
                booking_code: bkInputValue.toUpperCase(),
                isNew: true,
                customer_name: 'Nhập thông tin...' 
            });
        }
        return options;
    }, [bkOptions, bkInputValue]);

    const labelStyle = { 
        fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem', display: 'block' 
    };

    return (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, mb: -1.5 }}>
            
            <Box sx={{ flex: 2 }}>
                <Typography sx={labelStyle}>Tìm kiếm chung (Tên, SĐT, Mã BK)</Typography>
                <Autocomplete
                    size="small"
                    fullWidth
                    disabled={isLocked}
                    loading={globalLoading}
                    options={globalOptions}
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    getOptionLabel={(option) => {
                        if (option.dataType === 'CUSTOMER') return `${option.name} - ${option.phone}`;
                        if (option.dataType === 'BOOKING') return `${option.booking_code} - ${option.customer_name}`;
                        return '';
                    }}
                    filterOptions={(x) => x} 
                    onInputChange={(e, val) => fetchGlobalSearch(val)}
                    onChange={(e, val) => {
                        if (val) {
                            if (val.dataType === 'CUSTOMER') onSelectCustomer(val);
                            else if (val.dataType === 'BOOKING') onSelectBooking(val);
                            setOpen(false);
                        }
                    }}
                    renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                        <li key={key} {...otherProps} style={{ display: 'block', padding: '8px 12px', borderBottom: '1px solid #f5f5f5' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
                                    {option.dataType === 'CUSTOMER' ? 
                                        <PersonIcon sx={{ color: '#007373', fontSize: 20 }} /> : 
                                        <ConfirmationNumberIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                                    }
                                    <Box>
                                        {option.dataType === 'CUSTOMER' ? (
                                            <>
                                                <Typography variant="body2" fontWeight="bold">{option.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">SĐT: {option.phone}</Typography>
                                            </>
                                        ) : (
                                            <>
                                                <Typography variant="body2" fontWeight="bold" color="#ed6c02">
                                                    {option.booking_code}
                                                </Typography>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {option.customer_name} • {option.start_date}
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                                
                                {option.dataType === 'CUSTOMER' ? (
                                    <Chip 
                                        icon={<PersonAddIcon style={{ fontSize: 16 }} />} 
                                        label="Tạo Mới" 
                                        size="small" 
                                        color="primary" 
                                        variant="outlined"
                                        clickable
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onSelectCustomer(option);
                                            setOpen(false);
                                        }}
                                        sx={{ height: 24, fontSize: '0.7rem', flexShrink: 0 }}
                                    />
                                ) : (
                                    <Chip 
                                        icon={<EditIcon style={{ fontSize: 16 }} />} 
                                        label="Xem/Sửa" 
                                        size="small" 
                                        color="warning" 
                                        variant="outlined"
                                        clickable
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onSelectBooking(option);
                                            setOpen(false);
                                        }}
                                        sx={{ height: 24, fontSize: '0.7rem', flexShrink: 0 }}
                                    />
                                )}
                            </Box>
                        </li>
                    )}}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Nhập tên khách, SĐT hoặc mã booking..."
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <>
                                        {globalLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }}
                        />
                    )}
                />
            </Box>

            <Box sx={{ flex: 1 }}>
                <Typography sx={labelStyle}>Mã Khách hàng</Typography>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="---"
                    value={currentCustomer ? (currentCustomer.customer_code || currentCustomer.id) : ''}
                    InputProps={{ readOnly: true, sx: { bgcolor: '#f9f9f9' } }}
                />
            </Box>

            <Box sx={{ flex: 1.5 }}>
                <Typography sx={labelStyle}>Tìm/ Nhập Mã Booking</Typography>
                <Autocomplete
                    size="small"
                    fullWidth
                    freeSolo 
                    disabled={isLocked}
                    loading={bkLoading}
                    options={computedBkOptions}
                    getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.booking_code; 
                    }}
                    onInputChange={(e, val) => {
                        setBkInputValue(val);
                        fetchBookingsOnly(val);
                    }}
                    onChange={(e, val) => {
                        if (typeof val === 'string') {
                             onSelectBooking({ isNew: true, code: val.toUpperCase() });
                        } 
                        else if (val && val.isNew) {
                            onSelectBooking({ isNew: true, code: val.booking_code });
                        }
                        else if (val && val.booking_code) {
                            onSelectBooking(val);
                        }
                    }}
                    value={currentBookingCode || null}
                    renderOption={(props, option) => {
                        if (option.isNew) {
                            return (
                                <li {...props} key="create-new" style={{borderTop: '1px dashed #ccc', backgroundColor: '#e8f5e9'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', py: 1, color: '#2e7d32' }}>
                                        <PostAddIcon />
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                Tạo mới BK: {option.booking_code}
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                Mã này chưa có trong hệ thống. Nhấn để tạo.
                                            </Typography>
                                        </Box>
                                    </Box>
                                </li>
                            );
                        }
                        return (
                            <li {...props} key={option.booking_code}>
                                <Box sx={{ width: '100%' }}>
                                    <Typography variant="body2" fontWeight="bold" color="primary">
                                        {option.booking_code}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.customer_name} • {option.start_date}
                                    </Typography>
                                </Box>
                            </li>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {bkLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }}
                        />
                    )}
                />
            </Box>
        </Box>
    );
};

export default FastSearchSection;